/**
 * WorldBuilderPanel — Dockable panel for viewing and editing the
 * AI-inferred world-building fields (setting, era, magic, society,
 * geography + narrative axes).
 *
 * Tabs:
 *   World Detail — factual fields written by the Bible Inference Engine
 *   Narrative    — the four structural axes (proximity, conflict, tone, integration)
 *
 * Syncs with WorldBuilderDialog via shared MongoDB storage.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Globe, Loader2, Save, Sparkles, ExternalLink, BookOpenText, Layers,
} from 'lucide-react';
import { PanelFrame } from '../workspace/PanelFrame';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { usePW } from '@/lib/store';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Field definitions ─────────────────────────────────────────────────────
const DETAIL_FIELDS = [
  { key: 'setting',      label: 'Setting',           rows: 3,
    hint: 'Primary setting — city, region, planet, or social environment where the story takes place.' },
  { key: 'era',          label: 'Era / Period',       rows: 2,
    hint: 'Time period or world age — historical era, future century, or invented calendar.' },
  { key: 'magic_system', label: 'Magic / Tech System', rows: 3,
    hint: 'Rules governing magic, technology, or any speculative system. Leave blank for contemporary fiction.' },
  { key: 'society',      label: 'Society & Power',    rows: 3,
    hint: 'Social structure, power dynamics, key factions, class divides, and cultural norms.' },
  { key: 'geography',    label: 'Key Locations',      rows: 3,
    hint: 'Key locations and their narrative significance — where events unfold and why each place matters.' },
];

const NARRATIVE_AXES = [
  { key: 'proximity_layers',    label: 'Proximity Layers',    rows: 3,
    hint: 'Character-to-world proximity: inner (personal stakes), middle (community), outer (world / cosmos).' },
  { key: 'mechanics_of_conflict', label: 'Mechanics of Conflict', rows: 3,
    hint: 'The rules of tension in the story — what escalates it, what resolves it, recurring pressures.' },
  { key: 'tone',                label: 'Tone',                rows: 2,
    hint: 'The overall emotional weather. Adjectives + a brief phrase.' },
  { key: 'integration',         label: 'Integration',         rows: 3,
    hint: 'How world elements weave through plot, character, and theme — motifs, symbols, structural echoes.' },
];

const ALL_KEYS = [...DETAIL_FIELDS, ...NARRATIVE_AXES].map(f => f.key);

function emptyFields() {
  return Object.fromEntries(ALL_KEYS.map(k => [k, '']));
}

function normalise(fields, worldDetail) {
  const merged = { ...emptyFields(), ...(fields || {}), ...(worldDetail || {}) };
  return merged;
}

// ── Filled count badge ────────────────────────────────────────────────────
function useFilled(draft) {
  return useMemo(() => ALL_KEYS.filter(k => (draft[k] || '').trim().length > 0).length, [draft]);
}

// ── Tab bar ───────────────────────────────────────────────────────────────
const TABS = [
  { id: 'detail',    icon: Globe,         label: 'World Detail' },
  { id: 'narrative', icon: Layers,        label: 'Narrative' },
];

function TabBar({ active, onChange }) {
  return (
    <div className="flex shrink-0 gap-0 overflow-x-auto border-b border-panel-border">
      {TABS.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          data-testid={`wb-tab-${id}`}
          onClick={() => onChange(id)}
          className={cn(
            'flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
            active === id
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────
function EmptyState({ deducing, onDeduce }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
      <Globe className="h-10 w-10 text-muted-foreground/40" />
      <div>
        <p className="text-[13px] font-semibold">No world data yet</p>
        <p className="mt-1 max-w-[260px] text-[13px] leading-snug text-muted-foreground">
          World Detail fields (setting, era, magic, society, geography) are filled by the Bible Inference Engine when you import a synopsis. Narrative axes can be deduced from drafted chapters.
        </p>
      </div>
      <Button
        size="sm"
        className="gap-1.5"
        onClick={onDeduce}
        disabled={deducing}
        data-testid="wb-deduce-empty-btn"
      >
        {deducing
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Sparkles className="h-3.5 w-3.5" />}
        {deducing ? 'Deducing…' : 'Deduce Narrative Axes'}
      </Button>
      <button
        className="text-[12px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
        onClick={() => window.dispatchEvent(new CustomEvent('loom:open-world-builder'))}
      >
        Or open the full World Builder
      </button>
    </div>
  );
}

// ── Fields tab ────────────────────────────────────────────────────────────
function FieldsTab({ fields, onChange, defs }) {
  return (
    <div className="flex-1 overflow-y-auto space-y-4 p-3">
      {defs.map(({ key, label, rows, hint }) => (
        <div key={key} className="space-y-1" data-testid={`wb-field-${key}`}>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </label>
          <p className="text-[11px] text-muted-foreground/70 italic leading-tight">{hint}</p>
          <Textarea
            value={fields[key] || ''}
            onChange={e => onChange(key, e.target.value)}
            rows={rows}
            className="resize-y text-[13px]"
            style={{ minHeight: `${rows * 24 + 16}px` }}
            data-testid={`wb-input-${key}`}
          />
        </div>
      ))}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────
export function WorldBuilderPanel({ onClose, maximized, onMaximize }) {
  const { project } = usePW();
  const pid = project?.id;

  const [tab,      setTab]      = useState('detail');
  const [draft,    setDraft]    = useState(emptyFields);
  const [dirty,    setDirty]    = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [deducing, setDeducing] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/projects/${pid}/world-builder`, {
        withCredentials: true,
      });
      setDraft(normalise(r.data?.fields, r.data?.world_detail));
      setDirty(false);
    } catch {
      /* soft fail — empty form */
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  // Also sync when project.world_builder changes externally (e.g. WorldBuilderDialog saves)
  useEffect(() => {
    if (!project?.world_builder) return;
    const wb = project.world_builder;
    setDraft(prev => {
      const updated = { ...prev };
      ALL_KEYS.forEach(k => { if (wb[k] != null) updated[k] = wb[k]; });
      return updated;
    });
    setDirty(false);
  }, [project?.world_builder]);

  const hasData = useMemo(
    () => ALL_KEYS.some(k => (draft[k] || '').trim().length > 0),
    [draft],
  );

  const filled = useFilled(draft);

  // ── Change handler ────────────────────────────────────────────────────
  const handleChange = useCallback((key, val) => {
    setDraft(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  }, []);

  // ── Deduce ────────────────────────────────────────────────────────────
  const handleDeduce = useCallback(async () => {
    if (!pid) return;
    setDeducing(true);
    try {
      const r = await axios.post(
        `${API}/api/projects/${pid}/world-builder/deduce`,
        {},
        { withCredentials: true, timeout: 90000 },
      );
      const suggested = r.data?.suggested || {};
      if (Object.keys(suggested).length === 0) {
        toast.warning('No suggestions returned — draft at least one chapter first.');
        return;
      }
      setDraft(prev => {
        const next = { ...prev };
        Object.entries(suggested).forEach(([k, v]) => { if (v) next[k] = String(v); });
        return next;
      });
      setDirty(true);
      toast.success(`${Object.keys(suggested).length} world fields deduced — review and save.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Deduction failed.';
      toast.error(msg.includes('draft') || msg.includes('chapter') ? msg : 'Deduction failed — try drafting a chapter first.');
    } finally {
      setDeducing(false);
    }
  }, [pid]);

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!pid || !dirty) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/api/projects/${pid}/world-builder`,
        { ...draft },
        { withCredentials: true },
      );
      setDirty(false);
      toast.success('World Builder saved.');
      window.dispatchEvent(new CustomEvent('loom:world-builder-saved'));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [pid, dirty, draft]);

  const badgeLabel = `${filled}/${ALL_KEYS.length}`;

  return (
    <PanelFrame
      title="World Builder"
      icon={Globe}
      onClose={onClose}
      onMaximize={onMaximize}
      maximized={maximized}
      testId="world-builder-panel"
      badge={badgeLabel}
    >
      <div className="flex h-full flex-col">
        {/* Action bar */}
        <div className="flex shrink-0 items-center gap-1.5 border-b border-panel-border bg-panel-header/40 px-2 py-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 px-2 text-[12px]"
            onClick={handleDeduce}
            disabled={deducing || loading}
            data-testid="wb-deduce-btn"
            title="Deduce narrative axes (proximity, conflict, tone, integration) from drafted chapters"
          >
            {deducing
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Sparkles className="h-3 w-3" />}
            {deducing ? 'Deducing…' : 'Deduce Narrative'}
          </Button>
          <Button
            size="sm"
            variant={dirty ? 'default' : 'outline'}
            className="h-7 gap-1.5 px-2 text-[12px]"
            onClick={handleSave}
            disabled={saving || !dirty}
            data-testid="wb-save-btn"
          >
            {saving
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Save className="h-3 w-3" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <button
            className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => window.dispatchEvent(new CustomEvent('loom:open-world-builder'))}
            data-testid="wb-open-full-btn"
            title="Open full World Builder dialog"
          >
            Full editor <ExternalLink className="h-3 w-3" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
          </div>
        ) : !hasData ? (
          <EmptyState deducing={deducing} onDeduce={handleDeduce} />
        ) : (
          <>
            <TabBar active={tab} onChange={setTab} />
            {tab === 'detail' && (
              <>
                <p className="shrink-0 px-3 pt-2 pb-1 text-[11px] text-muted-foreground/70 italic">
                  Filled by the Bible Inference Engine on synopsis import — edit freely.
                </p>
                <FieldsTab
                  fields={draft}
                  onChange={handleChange}
                  defs={DETAIL_FIELDS}
                />
              </>
            )}
            {tab === 'narrative' && (
              <>
                <p className="shrink-0 px-3 pt-2 pb-1 text-[11px] text-muted-foreground/70 italic">
                  Structural axes deduced from manuscript chapters — use "Deduce Narrative" to fill.
                </p>
                <FieldsTab
                  fields={draft}
                  onChange={handleChange}
                  defs={NARRATIVE_AXES}
                />
              </>
            )}
          </>
        )}
      </div>
    </PanelFrame>
  );
}
